import { createI18n } from 'vue-i18n'
import type { LocaleMessageValue } from 'vue-i18n'
import { datetimeFormats, numberFormats } from './formats'

export const SUPPORTED_LOCALES = ['hr', 'sl', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: AppLocale = 'hr'

export const LOCALE_LABELS: Record<AppLocale, string> = {
    hr: 'Hrvatski',
    sl: 'Slovenščina',
    en: 'English',
}

export function normalizeLocale(value: string | null | undefined): AppLocale {
    return SUPPORTED_LOCALES.includes(value as AppLocale) ? (value as AppLocale) : DEFAULT_LOCALE
}

function loadLocale(modules: Record<string, { default: Record<string, unknown> }>): LocaleMessageValue {
    return Object.values(modules).reduce(
        (acc, mod) => ({ ...acc, ...mod.default }),
        {} as Record<string, unknown>,
    ) as LocaleMessageValue
}

const hr = loadLocale(import.meta.glob('./locales/hr/*.ts', { eager: true }))
const sl = loadLocale(import.meta.glob('./locales/sl/*.ts', { eager: true }))
const en = loadLocale(import.meta.glob('./locales/en/*.ts', { eager: true }))

const initialLocale = normalizeLocale(localStorage.getItem('user-language'))

// NOTE: fields cast to `any` so this file type-checks identically under `vue-tsc -b`
// (this app) and `vue-tsc --noEmit` (the others) — vue-i18n's createI18n overloads
// otherwise resolve differently under project-reference build mode and demand a
// default 'en-US' schema. Runtime behaviour is unchanged.
export const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: initialLocale,
    fallbackLocale: DEFAULT_LOCALE,
    messages: { hr, sl, en } as any,
    datetimeFormats: datetimeFormats as any,
    numberFormats: numberFormats as any,
})

export function setI18nLocale(value: string): AppLocale {
    const locale = normalizeLocale(value)
    ;(i18n.global.locale as unknown as { value: string }).value = locale
    localStorage.setItem('user-language', locale)
    document.documentElement.setAttribute('lang', locale)
    return locale
}

document.documentElement.setAttribute('lang', initialLocale)
